import { useRef, useEffect, useCallback, useMemo, memo, forwardRef, useImperativeHandle, useState } from 'react'
import TimelineDateGroup from './TimelineDateGroup'
import LoadingIndicator from './LoadingIndicator'
import FloatingDateHeader from './FloatingDateHeader'
import { useTimelineVirtualiser } from './useTimelineVirtualiser'
import { toLocalDateString } from '@/utils/dateUtils'

/**
 * Timeline - Virtualised container with lazy loading for past sales
 *
 * Renders a virtualised list of sales grouped by date using @tanstack/react-virtual.
 * Only ~500 DOM nodes are rendered regardless of total data size.
 *
 * @param {Object} props
 * @param {Array} props.pastItems - Array of timeline items sorted descending by date
 * @param {Function} props.onLoadMorePast - Called when scrolling down to load more past items
 * @param {boolean} props.loadingPast - Whether past items are currently being loaded
 * @param {boolean} props.hasMorePast - Whether more past items are available
 * @param {Function} props.onStatusChange - Status change handler: (itemId, newStatus) => void
 * @param {Function} props.onEdit - Edit handler: (item) => void
 * @param {Function} props.onViewInvoice - View invoice handler: (item) => void
 * @param {boolean} props.initialLoadComplete - Whether the first network fetch has settled
 * @param {string} props.className - Additional CSS classes
 * @param {React.Ref} ref - Ref exposing scrollToTop() method
 */
const Timeline = memo(forwardRef(function Timeline({
  pastItems = [],
  onLoadMorePast,
  loadingPast = false,
  hasMorePast = true,
  onConfirm,
  onDismiss,
  onQuantityChange,
  onStatusChange,
  onEdit,
  onEditRecurringOrder,
  onViewInvoice,
  onCustomerClick,
  onAdjustmentChange,
  onAcceptAdjustments,
  productTypes,
  confirmingIds = [],
  initialLoadComplete = false,
  className = ''
}, ref) {
  const containerRef = useRef(null)
  const [hasInitiallyScrolled, setHasInitiallyScrolled] = useState(false)
  const [isSettling, setIsSettling] = useState(false)
  const settlingTimerRef = useRef(null)

  // Cleanup settling timer on unmount
  useEffect(() => {
    return () => clearTimeout(settlingTimerRef.current)
  }, [])

  /**
   * Group items by date
   */
  const groupItemsByDate = useCallback((items) => {
    const grouped = new Map()

    items.forEach(item => {
      const date = item.date
      if (!grouped.has(date)) {
        grouped.set(date, [])
      }
      grouped.get(date).push(item)
    })

    return Array.from(grouped.entries()).map(([date, items]) => ({
      date,
      items
    }))
  }, [])

  const pastGroups = useMemo(() => groupItemsByDate(pastItems), [pastItems, groupItemsByDate])

  // Virtualiser hook
  const {
    virtualizer,
    flatList,
    stickyHeaderData,
    totalSize,
    virtualItems,
  } = useTimelineVirtualiser({
    pastGroups,
    scrollContainerRef: containerRef,
    hasMorePast,
    loadingPast,
    onLoadMorePast,
    hasInitiallyScrolled,
    isSettling,
  })

  // Expose scrollToTop method via ref
  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      const container = containerRef.current
      if (!container) return
      container.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }), [])

  // After data loads, jump to today's date group so the user lands on the
  // current day rather than the most distant future-confirmed sale at the top.
  const initialScrollStartedRef = useRef(false)

  useEffect(() => {
    if (initialScrollStartedRef.current) return
    if (!initialLoadComplete) return
    if (flatList.length === 0) return

    initialScrollStartedRef.current = true

    // flatList is sorted date-descending. Find the first group on or before
    // today; falls back to 0 if every group is in the past (then top is today
    // or older anyway).
    const todayStr = toLocalDateString(new Date())
    const todayIdx = flatList.findIndex(entry => entry.date <= todayStr)
    const targetIdx = todayIdx === -1 ? 0 : todayIdx

    if (targetIdx > 0) {
      virtualizer.scrollToIndex(targetIdx, { align: 'start' })
    }

    setHasInitiallyScrolled(true)
    setIsSettling(true)
    clearTimeout(settlingTimerRef.current)
    settlingTimerRef.current = setTimeout(() => setIsSettling(false), 2000)
  }, [initialLoadComplete, flatList, virtualizer])

  /**
   * Render a single virtual item (TimelineDateGroup)
   */
  const renderVirtualItem = useCallback((entry) => {
    return (
      <TimelineDateGroup
        date={entry.date}
        items={entry.items}
        onConfirm={onConfirm}
        onDismiss={onDismiss}
        onQuantityChange={onQuantityChange}
        onStatusChange={onStatusChange}
        onEdit={onEdit}
        onEditRecurringOrder={onEditRecurringOrder}
        onViewInvoice={onViewInvoice}
        onCustomerClick={onCustomerClick}
        onAdjustmentChange={onAdjustmentChange}
        onAcceptAdjustments={onAcceptAdjustments}
        productTypes={productTypes}
        confirmingIds={confirmingIds}
      />
    )
  }, [onConfirm, onDismiss, onQuantityChange, onStatusChange, onEdit, onEditRecurringOrder, onViewInvoice, onCustomerClick, onAdjustmentChange, onAcceptAdjustments, productTypes, confirmingIds])

  return (
    <div
      ref={containerRef}
      className={`overflow-y-auto ${className}`}
      style={{ overflowAnchor: 'none' }}
    >
      <FloatingDateHeader
        date={stickyHeaderData.currentDate}
        totalRevenue={stickyHeaderData.currentItems.reduce((sum, item) => sum + (item.total || 0), 0)}
        totalKg={stickyHeaderData.currentItems.reduce((sum, item) =>
          sum + (item.items || []).reduce((s, li) => s + (li.quantity || 0), 0), 0)}
        pushOffset={stickyHeaderData.pushOffset}
      />

      <div style={{ height: totalSize, width: '100%', position: 'relative' }}>
        {virtualItems.map(vItem => {
          const entry = flatList[vItem.index]
          return (
            <div
              key={vItem.key}
              data-index={vItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${vItem.start}px)`,
              }}
              className="timeline-past"
            >
              {renderVirtualItem(entry)}
            </div>
          )
        })}
      </div>

      {loadingPast && <LoadingIndicator text="Loading more sales..." />}

      {/* Empty State */}
      {flatList.length === 0 && !loadingPast && (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <p>No sales records found</p>
        </div>
      )}
    </div>
  )
}))

export default Timeline
